import { z } from "zod";
import { emailSchema, timestampSchema, uuidSchema } from "./api.ts";
import { currencyCodeSchema } from "./money.ts";
import { permissionSchema } from "./permissions.ts";

/**
 * Auth API contract — /api/v1/auth/*
 *
 * Staff users authenticate with an httpOnly session cookie ("l2a_session").
 * Every state-changing request with cookie auth must send the CSRF header
 * `x-l2a-csrf` whose value equals the non-httpOnly "l2a_csrf" cookie
 * (double-submit). API keys (Authorization: Bearer l2a_sk_…) skip CSRF.
 */

export const passwordSchema = z
  .string()
  .min(10, "Use at least 10 characters")
  .max(128)
  .refine((p) => /[a-zA-Z]/.test(p) && /[0-9]/.test(p), "Use letters and numbers");

export const signupInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
  password: passwordSchema,
  organizationName: z.string().trim().min(1).max(120),
  country: z.string().length(2).default("IN"),
  baseCurrency: currencyCodeSchema.default("INR"),
  timezone: z.string().max(64).default("Asia/Kolkata"),
  /**
   * The CAPTCHA widget's token.
   *
   * Optional in the schema because most deployments are invite-only and have
   * no challenge configured. The server decides whether it is required: with a
   * provider set, a missing token is refused on this field.
   */
  captchaToken: z.string().max(4000).optional(),
});
export type SignupInput = z.infer<typeof signupInputSchema>;

/** What the sign-up and forgot-password pages need to render a challenge. */
export const authChallengeSchema = z.object({
  provider: z.enum(["none", "turnstile", "hcaptcha"]),
  /** Public half of the key pair; null when no challenge is configured. */
  siteKey: z.string().nullable(),
  /** So the page can say "ask your administrator for an invitation" instead of showing a dead form. */
  signupMode: z.enum(["open", "invite_only", "closed"]),
});
export type AuthChallenge = z.infer<typeof authChallengeSchema>;

export const loginInputSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(128),
  totpCode: z.string().regex(/^\d{6}$/).optional(),
  recoveryCode: z.string().max(32).optional(),
  rememberMe: z.boolean().default(true),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

export const membershipSummarySchema = z.object({
  orgId: uuidSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  logoUrl: z.string().nullable(),
  roleKey: z.string(),
  roleName: z.string(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

export const meSchema = z.object({
  user: z.object({
    id: uuidSchema,
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    emailVerified: z.boolean(),
    twoFactorEnabled: z.boolean(),
    timezone: z.string(),
    locale: z.string(),
    createdAt: timestampSchema,
  }),
  org: z.object({
    id: uuidSchema,
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
    baseCurrency: currencyCodeSchema,
    country: z.string(),
    timezone: z.string(),
    fyStartMonth: z.number().int().min(1).max(12),
    dateFormat: z.string(),
    plan: z.string(),
  }),
  role: z.object({ id: uuidSchema, key: z.string(), name: z.string() }),
  permissions: z.array(permissionSchema),
  memberships: z.array(membershipSummarySchema),
  /**
   * The session this was read through. `viaSso` is true when a workspace's
   * identity provider opened it: such a session stays in that workspace and
   * cannot change the account's password or second factor, so the security
   * page does not offer those forms.
   */
  session: z.object({ viaSso: z.boolean() }),
});
export type Me = z.infer<typeof meSchema>;

/** Login either succeeds (session cookie set, returns Me) or asks for a second factor. */
export const loginResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), me: meSchema }),
  z.object({ status: z.literal("two_factor_required") }),
]);
export type LoginResult = z.infer<typeof loginResultSchema>;

export const switchOrgInputSchema = z.object({ orgId: uuidSchema });
export const forgotPasswordInputSchema = z.object({
  email: emailSchema,
  /** Same rule as sign-up: required only where a provider is configured. */
  captchaToken: z.string().max(4000).optional(),
});
export const resetPasswordInputSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });
export const verifyEmailInputSchema = z.object({ token: z.string().min(20).max(200) });
export const changePasswordInputSchema = z.object({ currentPassword: z.string().min(1).max(128), newPassword: passwordSchema });
export const updateProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  timezone: z.string().max(64).optional(),
  locale: z.string().max(16).optional(),
  avatarUrl: z.string().url().nullable().optional(),
});

export const sessionInfoSchema = z.object({
  id: uuidSchema,
  current: z.boolean(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: timestampSchema,
  lastSeenAt: timestampSchema,
  expiresAt: timestampSchema,
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;

export const twoFactorSetupSchema = z.object({ secret: z.string(), otpauthUrl: z.string(), qrDataUrl: z.string() });
export const twoFactorEnableInputSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const twoFactorEnableResultSchema = z.object({ recoveryCodes: z.array(z.string()) });
export const twoFactorDisableInputSchema = z.object({ password: z.string().min(1).max(128), code: z.string().regex(/^\d{6}$/) });

export const invitationPreviewSchema = z.object({
  orgName: z.string(),
  inviterName: z.string(),
  email: z.string(),
  roleName: z.string(),
  userExists: z.boolean(),
  expiresAt: timestampSchema,
});
export const acceptInvitationInputSchema = z.object({
  token: z.string().min(20).max(200),
  name: z.string().trim().min(1).max(120).optional(),
  password: passwordSchema.optional(),
});

/** Client-portal (contact) auth — /api/portal/v1/auth/* — passwordless magic links. */
export const portalRequestLinkInputSchema = z.object({ email: emailSchema, orgSlug: z.string().max(64) });
export const portalVerifyInputSchema = z.object({ token: z.string().min(20).max(200) });
export const portalMeSchema = z.object({
  contact: z.object({ id: uuidSchema, name: z.string(), email: z.string() }),
  client: z.object({ id: uuidSchema, name: z.string() }),
  org: z.object({ id: uuidSchema, name: z.string(), slug: z.string(), logoUrl: z.string().nullable(), brandColor: z.string().nullable() }),
  permissions: z.array(z.string()),
});
export type PortalMe = z.infer<typeof portalMeSchema>;
